import {
  PersonAddRounded as AddUserIcon,
  CloseRounded as CloseIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  RestoreRounded as RestoreIcon,
} from "@mui/icons-material";
import {
  Paper,
  Grid,
  Stack,
  Typography,
  Dialog,
  DialogContent,
  DialogTitle,
  TextField,
  Box,
  Tooltip,
  IconButton,
  MenuItem,
} from "@mui/material";
import Component from "components/Component";
import Button from "components/common/Button";
import DefaultLoader from "components/common/DefaultLoader";
import InlineSearch from "components/common/InlineSearch";
import { PersonMonogram } from "components/inputs/program/PersonSummary";
import { Formik, Form, Field } from "formik";
import { useSnackbar } from "notistack";
import { useState, useEffect } from "react";
import {
  useConfigQuery,
  useAdminUpdateConfigMutation,
} from "services/staticDataService";
import * as Yup from "yup";

const LOCATION_OPTIONS = [
  "Bermuda",
  "London",
  "New York",
  "Miami",
  "Atlanta",
  "Chicago",
  "Los Angeles",
  "Toronto",
];

const UnderwriterRow = ({
  underwriter,
  onEdit,
  onDelete,
  onReactivate,
  isUpdating,
}) => {
  return (
    <Paper
      variant={"outlined"}
      sx={{
        padding: "0.5rem 1rem 0.75rem 1rem",
        marginBottom: "0.75rem",
        opacity: underwriter?.isDeleted ? 0.6 : 1,
        backgroundColor: underwriter?.isDeleted ? "#f5f5f5" : "inherit",
        "&:hover .edit-icon": {
          visibility: "visible",
        },
        "&:hover .delete-icon": {
          visibility: "visible",
        },
        "&:hover .reactivate-icon": {
          visibility: "visible",
        },
      }}
    >
      <Grid container alignItems={"center"} spacing={1}>
        <Grid item xs={1}>
          <PersonMonogram person={{ name: underwriter?.name }} />
        </Grid>
        <Grid item xs={2}>
          <Tooltip
            title={
              underwriter?.name + (underwriter?.isDeleted ? " (Deleted)" : "")
            }
            placement={"top"}
          >
            <Typography
              variant={"body1"}
              sx={{
                textDecoration: underwriter?.isDeleted
                  ? "line-through"
                  : "none",
                color: underwriter?.isDeleted ? "text.disabled" : "inherit",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {underwriter?.name}
              {underwriter?.isDeleted && " (Deleted)"}
            </Typography>
          </Tooltip>
        </Grid>
        <Grid item xs={3}>
          <Tooltip title={underwriter?.email} placement={"top"}>
            <Typography
              variant={"body2"}
              color={underwriter?.isDeleted ? "text.disabled" : "textSecondary"}
              sx={{
                textDecoration: underwriter?.isDeleted
                  ? "line-through"
                  : "none",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {underwriter?.email}
            </Typography>
          </Tooltip>
        </Grid>
        <Grid item xs={1}>
          <Tooltip title={underwriter?.telephone} placement={"top"}>
            <Typography
              variant={"body2"}
              color={underwriter?.isDeleted ? "text.disabled" : "textSecondary"}
              sx={{
                textDecoration: underwriter?.isDeleted
                  ? "line-through"
                  : "none",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {underwriter?.telephone || "-"}
            </Typography>
          </Tooltip>
        </Grid>
        <Grid item xs={1}>
          <Tooltip title={underwriter?.title} placement={"top"}>
            <Typography
              variant={"body2"}
              color={underwriter?.isDeleted ? "text.disabled" : "textSecondary"}
              sx={{
                textDecoration: underwriter?.isDeleted
                  ? "line-through"
                  : "none",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {underwriter?.title || "-"}
            </Typography>
          </Tooltip>
        </Grid>
        <Grid item xs={1}>
          <Tooltip title={underwriter?.location} placement={"top"}>
            <Typography
              variant={"body2"}
              color={underwriter?.isDeleted ? "text.disabled" : "textSecondary"}
              sx={{
                textDecoration: underwriter?.isDeleted
                  ? "line-through"
                  : "none",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {underwriter?.location || "-"}
            </Typography>
          </Tooltip>
        </Grid>
        <Grid item xs={2}>
          <Tooltip
            title={underwriter?.policyUnderwriter?.name}
            placement={"top"}
          >
            <Typography
              variant={"body2"}
              color={underwriter?.isDeleted ? "text.disabled" : "textSecondary"}
              sx={{
                textDecoration: underwriter?.isDeleted
                  ? "line-through"
                  : "none",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {underwriter?.policyUnderwriter?.name || "-"}
            </Typography>
          </Tooltip>
        </Grid>
        <Grid item xs={1}>
          <Stack direction={"row"} justifyContent={"flex-end"}>
            {!underwriter?.isDeleted ? (
              <>
                <Tooltip title={"Edit underwriter"}>
                  <span>
                    <IconButton
                      className={"edit-icon"}
                      sx={{
                        visibility: "hidden",
                      }}
                      onClick={() => onEdit(underwriter)}
                      disabled={isUpdating}
                    >
                      <EditIcon />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title={"Delete underwriter"}>
                  <span>
                    <IconButton
                      className={"delete-icon"}
                      sx={{
                        visibility: "hidden",
                      }}
                      onClick={() => onDelete(underwriter)}
                      disabled={isUpdating}
                      color={"error"}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </span>
                </Tooltip>
              </>
            ) : (
              <Tooltip title={"Reactivate underwriter"}>
                <span>
                  <IconButton
                    className={"reactivate-icon"}
                    sx={{
                      visibility: "hidden",
                      opacity: 1,
                    }}
                    onClick={() => onReactivate(underwriter)}
                    disabled={isUpdating}
                    color={"primary"}
                  >
                    <RestoreIcon />
                  </IconButton>
                </span>
              </Tooltip>
            )}
          </Stack>
        </Grid>
      </Grid>
    </Paper>
  );
};

const validationSchema = Yup.object({
  name: Yup.string()
    .trim()
    .min(2, "Name must be at least 2 characters")
    .max(50, "Name must be less than 50 characters")
    .required("Name is required"),
  email: Yup.string()
    .trim()
    .email("Invalid email format")
    .required("Email is required"),
  location: Yup.string().nullable(),
  telephone: Yup.string().nullable(),
  title: Yup.string().nullable(),
  policyUnderwriter: Yup.string().nullable(),
});

const UnderwriterDialog = ({
  open,
  close,
  updateConfig,
  isUpdating,
  allUnderwriters,
  underwriter = null,
}) => {
  const [loading, setLoading] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);

  useEffect(() => {
    if (open) {
      setIsEditMode(Boolean(underwriter));
    }
  }, [open, underwriter]);

  useEffect(() => {
    if (!isUpdating && loading) {
      close();
      setLoading(false);
    }
  }, [isUpdating, close, loading]);

  const handleSubmit = (values) => {
    const policyUnderwriterEmail = values.policyUnderwriter?.trim();
    const selectedUnderwriter = policyUnderwriterEmail
      ? allUnderwriters.find(
          (u) => u.email === policyUnderwriterEmail && !u.isDeleted
        )
      : null;

    updateConfig({
      ...(isEditMode && { id: underwriter.id }),
      name: values.name.trim(),
      email: values.email.trim(),
      location: values.location || null,
      telephone: values.telephone || null,
      title: values.title || null,
      ...(selectedUnderwriter
        ? {
            policyUnderwriter: {
              name: selectedUnderwriter.name,
              title: selectedUnderwriter.title || "",
              email: selectedUnderwriter.email,
            },
          }
        : {
            policyUnderwriter: null,
          }),
    });
    setLoading(true);
  };

  return (
    <Dialog open={open} onClose={close} fullWidth maxWidth={"sm"}>
      <DialogTitle>
        <Stack
          justifyContent={"space-between"}
          direction={"row"}
          alignItems={"center"}
        >
          <Typography variant={"h6"}>
            {isEditMode ? "Edit Underwriter" : "New Underwriter"}
          </Typography>
          <IconButton onClick={close}>
            <CloseIcon />
          </IconButton>
        </Stack>
      </DialogTitle>
      <DialogContent>
        <Formik
          initialValues={{
            name: underwriter?.name || "",
            email: underwriter?.email || "",
            location: underwriter?.location || "",
            telephone: underwriter?.telephone || "",
            title: underwriter?.title || "",
            policyUnderwriter: underwriter?.policyUnderwriter?.email || "",
          }}
          enableReinitialize
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({ errors, touched, isValid, dirty, submitForm }) => (
            <Form>
              <Grid container spacing={2} sx={{ paddingTop: 1 }}>
                <Grid item xs={12}>
                  <Field name={"name"}>
                    {({ field }) => (
                      <TextField
                        {...field}
                        label={"Name"}
                        variant={"outlined"}
                        fullWidth
                        disabled={loading}
                        error={touched.name && Boolean(errors.name)}
                        helperText={touched.name && errors.name}
                      />
                    )}
                  </Field>
                </Grid>
                <Grid item xs={12}>
                  <Field name={"email"}>
                    {({ field }) => (
                      <TextField
                        {...field}
                        label={"Email"}
                        variant={"outlined"}
                        fullWidth
                        disabled={loading || isEditMode}
                        type={"email"}
                        error={touched.email && Boolean(errors.email)}
                        helperText={touched.email && errors.email}
                      />
                    )}
                  </Field>
                </Grid>
                <Grid item xs={12}>
                  <Field name={"telephone"}>
                    {({ field }) => (
                      <TextField
                        {...field}
                        label={"Telephone"}
                        variant={"outlined"}
                        fullWidth
                        disabled={loading}
                        error={touched.telephone && Boolean(errors.telephone)}
                        helperText={touched.telephone && errors.telephone}
                      />
                    )}
                  </Field>
                </Grid>
                <Grid item xs={12}>
                  <Field name={"title"}>
                    {({ field }) => (
                      <TextField
                        {...field}
                        label={"Title"}
                        variant={"outlined"}
                        fullWidth
                        disabled={loading}
                        error={touched.title && Boolean(errors.title)}
                        helperText={touched.title && errors.title}
                      />
                    )}
                  </Field>
                </Grid>
                <Grid item xs={12}>
                  <Field name={"location"}>
                    {({ field }) => (
                      <TextField
                        {...field}
                        label={"Location"}
                        variant={"outlined"}
                        fullWidth
                        disabled={loading}
                        select
                        error={touched.location && Boolean(errors.location)}
                        helperText={touched.location && errors.location}
                      >
                        <MenuItem value={""}>{"None"}</MenuItem>
                        {LOCATION_OPTIONS.map((location) => (
                          <MenuItem key={location} value={location}>
                            {location}
                          </MenuItem>
                        ))}
                      </TextField>
                    )}
                  </Field>
                </Grid>
                <Grid item xs={12}>
                  <Field name={"policyUnderwriter"}>
                    {({ field }) => (
                      <TextField
                        {...field}
                        label={"Policy Underwriter"}
                        variant={"outlined"}
                        fullWidth
                        disabled={loading}
                        select
                        error={
                          touched.policyUnderwriter &&
                          Boolean(errors.policyUnderwriter)
                        }
                        helperText={
                          touched.policyUnderwriter && errors.policyUnderwriter
                        }
                      >
                        <MenuItem value={""}>{"None"}</MenuItem>
                        {allUnderwriters
                          .filter(
                            (u) => !u.isDeleted && u.id !== underwriter?.id
                          )
                          .map((u) => (
                            <MenuItem key={u.id} value={u.email}>
                              {u.name}
                            </MenuItem>
                          ))}
                      </TextField>
                    )}
                  </Field>
                </Grid>
                <Grid
                  item
                  container
                  xs={12}
                  justifyContent={"flex-end"}
                  alignItems={"center"}
                  sx={{ paddingTop: 2 }}
                >
                  <Grid item>
                    <Button
                      onClick={submitForm}
                      isLoading={loading}
                      isDisabled={loading || !isValid || !dirty}
                    >
                      {isEditMode ? "Update Underwriter" : "Create Underwriter"}
                    </Button>
                  </Grid>
                </Grid>
              </Grid>
            </Form>
          )}
        </Formik>
      </DialogContent>
    </Dialog>
  );
};

const UnderwritersConfig = () => {
  const [underwriters, setUnderwriters] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingUnderwriter, setEditingUnderwriter] = useState(null);
  const [saved, setSaved] = useState(false);

  const { enqueueSnackbar: queueMessage } = useSnackbar();
  const addMessage = (variant, message) => {
    queueMessage(message, {
      variant,
      autoHideDuration: 5_000,
      preventDuplicate: true,
    });
  };

  const {
    data: retrievedUnderwriters,
    isFetching: underwritersLoading,
    refetch,
  } = useConfigQuery("underwriters");

  const [
    triggerUpdateConfig,
    { isLoading: isUpdating },
  ] = useAdminUpdateConfigMutation();

  useEffect(() => {
    if (retrievedUnderwriters?.all) {
      const allUnderwriters = retrievedUnderwriters.all.map((u) => ({
        ...u,
        isDeleted: false,
      }));
      const deletedUnderwriters = (
        retrievedUnderwriters.deleted || []
      ).map((u) => ({ ...u, isDeleted: true }));
      setUnderwriters([...allUnderwriters, ...deletedUnderwriters]);
    }
  }, [retrievedUnderwriters]);

  const handleOpenDialog = (underwriter = null) => {
    setEditingUnderwriter(underwriter);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingUnderwriter(null);
  };

  useEffect(() => {
    refetch();
  }, [refetch]);

  const cleanUnderwriter = (underwriter) => {
    const { isDeleted, ...cleanData } = underwriter;
    return cleanData;
  };

  const updateConfig = (underwriterData) => {
    let updatedActiveUnderwriters;
    let updatedDeletedUnderwriters = retrievedUnderwriters?.deleted || [];

    if (underwriterData.id) {
      updatedActiveUnderwriters = underwriters
        .filter((u) => !u.isDeleted)
        .map((u) => {
          if (u.id === underwriterData.id) {
            return cleanUnderwriter({ ...u, ...underwriterData });
          }
          if (u.policyUnderwriter?.email === underwriterData.email) {
            return cleanUnderwriter({
              ...u,
              policyUnderwriter: {
                name: underwriterData.name,
                title: underwriterData.title || "",
                email: underwriterData.email,
              },
            });
          }
          return cleanUnderwriter(u);
        });

      updatedDeletedUnderwriters = (retrievedUnderwriters?.deleted || []).map(
        (u) => {
          if (u.policyUnderwriter?.email === underwriterData.email) {
            return {
              ...u,
              policyUnderwriter: {
                name: underwriterData.name,
                title: underwriterData.title || "",
                email: underwriterData.email,
              },
            };
          }
          return u;
        }
      );
    } else {
      const newUnderwriter = {
        id: crypto.randomUUID(),
        name: underwriterData.name,
        email: underwriterData.email,
        location: underwriterData.location || null,
        telephone: underwriterData.telephone || null,
        title: underwriterData.title || null,
        policyUnderwriter: underwriterData.policyUnderwriter || null,
      };
      updatedActiveUnderwriters = [
        ...underwriters.filter((u) => !u.isDeleted).map(cleanUnderwriter),
        newUnderwriter,
      ];
    }

    triggerUpdateConfig({
      key: "underwriters",
      value: {
        all: updatedActiveUnderwriters,
        deleted: updatedDeletedUnderwriters,
      },
    }).then((resp) => {
      if (resp?.error) {
        addMessage("error", "Failed to update underwriter");
        return;
      }

      if (resp?.data) {
        const allUnderwriters = resp.data.all.map((u) => ({
          ...u,
          isDeleted: false,
        }));
        const deletedUnderwriters = (resp.data.deleted || []).map((u) => ({
          ...u,
          isDeleted: true,
        }));
        setUnderwriters([...allUnderwriters, ...deletedUnderwriters]);
        clearTimeout(saved);
        setSaved(
          setTimeout(() => {
            setSaved(false);
          }, 5000)
        );
        if (!underwriterData.id) {
          addMessage("success", "Underwriter created successfully");
        } else {
          addMessage("success", "Underwriter updated successfully");
        }
      } else {
        addMessage("error", "Failed to update underwriter");
      }
    });
  };

  const deleteUnderwriter = (underwriter) => {
    const updatedActiveUnderwriters = underwriters
      .filter((u) => u.id !== underwriter.id && !u.isDeleted)
      .map(cleanUnderwriter);
    const updatedDeletedUnderwriters = [
      ...underwriters.filter((u) => u.isDeleted).map(cleanUnderwriter),
      cleanUnderwriter(underwriter),
    ];

    triggerUpdateConfig({
      key: "underwriters",
      value: {
        all: updatedActiveUnderwriters,
        deleted: updatedDeletedUnderwriters,
      },
    }).then((resp) => {
      if (resp?.data?.all !== undefined) {
        const allUnderwriters = resp.data.all.map((u) => ({
          ...u,
          isDeleted: false,
        }));
        const deletedUnderwriters = (resp.data.deleted || []).map((u) => ({
          ...u,
          isDeleted: true,
        }));
        setUnderwriters([...allUnderwriters, ...deletedUnderwriters]);
        addMessage("success", "Underwriter deleted successfully");
      } else {
        addMessage(
          "error",
          "Failed to delete underwriter, please contact support at: support@cactus.bm"
        );
      }
    });
  };

  const reactivateUnderwriter = (underwriter) => {
    const updatedActiveUnderwriters = [
      ...underwriters.filter((u) => !u.isDeleted).map(cleanUnderwriter),
      cleanUnderwriter(underwriter),
    ];
    const updatedDeletedUnderwriters = underwriters
      .filter((u) => u.isDeleted && u.id !== underwriter.id)
      .map(cleanUnderwriter);

    triggerUpdateConfig({
      key: "underwriters",
      value: {
        all: updatedActiveUnderwriters,
        deleted: updatedDeletedUnderwriters,
      },
    }).then((resp) => {
      if (resp?.data?.all !== undefined) {
        const allUnderwriters = resp.data.all.map((u) => ({
          ...u,
          isDeleted: false,
        }));
        const deletedUnderwriters = (resp.data.deleted || []).map((u) => ({
          ...u,
          isDeleted: true,
        }));
        setUnderwriters([...allUnderwriters, ...deletedUnderwriters]);
        addMessage("success", "Underwriter reactivated successfully");
      } else {
        addMessage(
          "error",
          "Failed to reactivate underwriter, please contact support at: support@cactus.bm"
        );
      }
    });
  };

  const searchedUnderwriters = underwriters.filter(
    (underwriter) =>
      underwriter?.name?.toLowerCase()?.includes(searchText.toLowerCase()) ||
      underwriter?.email?.toLowerCase()?.includes(searchText.toLowerCase())
  );

  return (
    <Component
      title={"Edit Underwriters"}
      loading={underwritersLoading}
      options={
        <Button
          size={"small"}
          startIcon={<AddUserIcon />}
          onClick={() => handleOpenDialog()}
        >
          {"Add Underwriter"}
        </Button>
      }
    >
      <Stack spacing={2}>
        <InlineSearch
          placeholder={"Quick Search"}
          onChange={(text) => setSearchText(text)}
        />

        {searchedUnderwriters?.length > 0 ? (
          <Grid container>
            <Grid
              item
              xs={12}
              sx={{
                marginBottom: 1,
                paddingLeft: "1rem",
                paddingRight: "1rem",
              }}
            >
              <Grid container alignItems={"center"} spacing={1}>
                <Grid item xs={1}></Grid>
                <Grid item xs={2}>
                  <Typography
                    variant={"caption"}
                    color={"textSecondary"}
                    fontWeight={600}
                  >
                    {"Name"}
                  </Typography>
                </Grid>
                <Grid item xs={3}>
                  <Typography
                    variant={"caption"}
                    color={"textSecondary"}
                    fontWeight={600}
                  >
                    {"Email"}
                  </Typography>
                </Grid>
                <Grid item xs={1}>
                  <Typography
                    variant={"caption"}
                    color={"textSecondary"}
                    fontWeight={600}
                  >
                    {"Telephone"}
                  </Typography>
                </Grid>
                <Grid item xs={1}>
                  <Typography
                    variant={"caption"}
                    color={"textSecondary"}
                    fontWeight={600}
                  >
                    {"Title"}
                  </Typography>
                </Grid>
                <Grid item xs={1}>
                  <Typography
                    variant={"caption"}
                    color={"textSecondary"}
                    fontWeight={600}
                  >
                    {"Location"}
                  </Typography>
                </Grid>
                <Grid item xs={2}>
                  <Typography
                    variant={"caption"}
                    color={"textSecondary"}
                    fontWeight={600}
                  >
                    {"Policy Underwriter"}
                  </Typography>
                </Grid>
                <Grid item xs={1}></Grid>
              </Grid>
            </Grid>
            {searchedUnderwriters.map((underwriter) => (
              <Grid key={underwriter.id} item xs={12}>
                <UnderwriterRow
                  underwriter={underwriter}
                  onEdit={handleOpenDialog}
                  onDelete={deleteUnderwriter}
                  onReactivate={reactivateUnderwriter}
                  isUpdating={isUpdating}
                />
              </Grid>
            ))}
          </Grid>
        ) : (
          <Box
            sx={{
              height: "40vh",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            {underwritersLoading ? (
              <DefaultLoader size={64} />
            ) : (
              <Typography>
                {searchText
                  ? `No matches found for '${searchText}'`
                  : "No underwriters configured"}
              </Typography>
            )}
          </Box>
        )}
      </Stack>

      <UnderwriterDialog
        open={dialogOpen}
        close={handleCloseDialog}
        updateConfig={updateConfig}
        isUpdating={isUpdating}
        allUnderwriters={underwriters}
        underwriter={editingUnderwriter}
      />
    </Component>
  );
};

export default UnderwritersConfig;
